import styled from 'styled-components';
import STATUS_CODE from '@/src/constants/statusCode';
import { quickCommerce as trackingQuickCommerce } from '@/src/utils/tracking/quickCommerce';
import productImpressionsLoaded from '@/src/utils/tracking/productImpressionsLoaded';
import productClicked from '@utils//tracking/productClicked';
import { ErrorVendorList } from '@components/ErrorVendorList/ErrorVendorList';
import { ScrolleableProductCardSVerticalSkeleton } from '@components/Skeleton/revamp/ProductCardSVerticalSkeleton';
import { VendorRowSkeleton } from '@components/Skeleton/revamp/VendorRowSkeleton';
import useGetVendorsQC from '@hooks/useGetVendorsQC';
import ProductCardSVertical from '@pedidosya/web-fenix/patterns/ProductCardSVertical';
import ProductSwimlaneSVertical from '@pedidosya/web-fenix/patterns/ProductSwimlaneSVertical';
import VendorRow from '@pedidosya/web-fenix/patterns/VendorRow';
import Highlight from '@pedidosya/web-fenix/system/Highlight';
import { getTagDiscount, getTagMarkup } from '@utils//utils';
import { keysToCamel } from '@utils//mapper/mapperToCamelCase';
import { createElement } from 'react';
import { InView } from 'react-intersection-observer';
import { getRedirectLink, redirectToDeeplink } from '../../utils/deeplink';
import { Space } from '../Space/Space';
import {
  ContainerErrorItems,
  ContainerSkeletonQC,
} from './QCPartnerList.styles';

const Wrapper = styled.div`
  padding-bottom: 16px;
`;

const QCPartnerList = ({
  landingId,
  countryId,
  latitude,
  longitude,
  landingType,
  userId,
  filters,
  headers,
  isDesktop,
  platform,
  landingTitle,
  bannerId,
  businessType,
  rankingRequestId,
  initialVendors,
  initialTotalVendors,
  initialSize,
  initialOriginalPeyaTrace,
  initialMidasRequestId,
  isMigration,
}) => {
  const {
    loading,
    vendorQcError,
    vendors,
    reloadLanding,
    redirectToHome,
    loader,
    totalVendors,
    size,
    page,
  } = useGetVendorsQC(
    landingId,
    countryId,
    latitude,
    longitude,
    landingType,
    userId,
    filters,
    headers,
    initialVendors,
    initialTotalVendors,
    initialSize,
    isMigration,
    initialOriginalPeyaTrace,
    initialMidasRequestId,
  );

  const skeletonKeyCounter = 0;

  return (
    <Wrapper>
      {keysToCamel(vendors)?.map((vendor, vendorPosition) => (
        <div key={vendor?.id}>
          <Space mt={12} />
          <VendorRow
            variant="header-off"
            title={vendor?.vendorName}
            rate={parseFloat(vendor?.rating)}
            labels={vendor?.labels}
            renderFavToggle={false}
            logo={`${vendor?.image}?width=100}`}
            delivery={vendor?.delivery}
            deliveryETAVariant={vendor?.deliveryEtaVariant}
            deliveryFeeVariant={vendor?.deliveryFeeVariant}
            ocassion={vendor?.occasion}
            renderPlusTag={vendor?.labels?.isLoyalty}
            rightIntern={vendor?.isNew ? 'tag-s' : 'rating'}
            rightInternLabel={vendor?.isNew ? 'Nuevo' : null}
            status={vendor?.status?.showOverlay ? 'closed' : 'open'}
            stateText={vendor?.status?.title}
            onClick={() => {
              trackingQuickCommerce({
                type: 'shop_clicked',
                payload: {
                  vendor,
                  vendorPosition,
                },
              });
              redirectToDeeplink(getRedirectLink(vendor));
            }}
          >
            {(vendor?.labels?.discountLabel || vendor?.labels?.markupLabel) &&
              createElement(VendorRow.TagGroup, {
                view: 'stacked',
                tags: [
                  vendor?.labels?.discountLabel?.trim() &&
                    getTagDiscount(
                      vendor?.labels?.isLoyalty,
                      vendor?.labels?.discountLabel,
                    ),
                  vendor?.labels?.markupLabel?.trim() &&
                    getTagMarkup(vendor?.labels?.markupLabel),
                ].filter(Boolean),
              })}
            {createElement(VendorRow.ServiceInfo, {
              showInfoTime: true,
              deliveryFeePrice: vendor?.delivery?.deliveryFeeWithDiscount,
              deliveryFeePriceOriginal: vendor?.delivery?.deliveryFee,
              deliveryTime: vendor?.delivery?.deliveryTime,
              pickupDistance: vendor?.delivery?.distance,
              pickupTime: vendor?.delivery?.pickupTime,
              kind: vendor?.occasion?.toLowerCase(),
              deliveryFeeVariant: vendor?.deliveryFeeVariant,
            })}
            {createElement(
              VendorRow.HighLightGroupBottom,
              {},
              [
                vendor?.labels?.sponsoringLabel &&
                  createElement(Highlight, {
                    label: vendor.labels.sponsoringLabel,
                  }),
                vendor?.labels?.conditionLabels?.[0] &&
                  createElement(Highlight, {
                    label: vendor.labels.conditionLabels[0],
                  }),
              ].filter(Boolean),
            )}
          </VendorRow>
          <ProductSwimlaneSVertical>
            <ProductSwimlaneSVertical.Elements data-testid="items-qc">
              {keysToCamel(vendor.products)?.map((product, position) => (
                <InView
                  key={product?.id}
                  as="div"
                  triggerOnce
                  onChange={(inView) => {
                    if (inView) {
                      productImpressionsLoaded(
                        bannerId,
                        businessType,
                        position,
                        product?.name,
                        product?.id,
                        vendor?.id,
                        rankingRequestId,
                      );
                    }
                  }}
                >
                  <ProductCardSVertical
                    bodyProps={{
                      infoComplementary: {
                        quantityByUnit: product?.size,
                        pricePerUnit: product?.pricing?.pricePerMeasurementUnit,
                      },
                      label: '',
                      layout: 'price-first',
                      price: product?.pricing?.price,
                      priceOriginal: product?.pricing?.beforePrice,
                      weightable: false,
                    }}
                    imageProps={{
                      aspectRatio: '1',
                      imageMode: 'fit',
                      src: `${product?.image}?width=135&height=135`,
                      srcSet: `${product?.image}?width=135&height=135&dpi=3 3x`,
                      withAdaptativeLoading: false,
                    }}
                    labelsProps={[
                      product?.tags?.[0] &&
                        getTagDiscount(false, product?.tags?.[0]?.text),
                    ].filter(Boolean)}
                    onContent={false}
                    onContainerCTA={() => {
                      productClicked({
                        shopId: vendor?.id,
                        discount: product?.tags?.[0]?.text,
                        productIndex: (position ?? 0) + 1,
                        productSku: product?.id,
                        productUnitPrice:
                          product?.pricing?.pricePerMeasurementUnit,
                        productUnitSalePrice: product?.pricing?.price,
                        landingId,
                        landingTitle,
                        landingType,
                      });
                      if (
                        isDesktop ||
                        platform === 'unknown' ||
                        platform === 'mweb'
                      ) {
                        redirectToDeeplink(vendor?.url);
                      } else {
                        redirectToDeeplink(getRedirectLink(product));
                      }
                    }}
                    state="enabled"
                    styleCard="default"
                    title={product?.name}
                  />
                </InView>
              ))}
            </ProductSwimlaneSVertical.Elements>
          </ProductSwimlaneSVertical>
        </div>
      ))}

      {loading && !vendorQcError ? (
        <>
          <Space mt={12} />
          {[...Array(9)].map((_, index) => {
            const containerKey = `skeleton_${skeletonKeyCounter + index}`;
            return (
              <ContainerSkeletonQC key={containerKey} data-testid="skeleton-qc">
                <VendorRowSkeleton />
                <ScrolleableProductCardSVerticalSkeleton />
              </ContainerSkeletonQC>
            );
          })}
        </>
      ) : null}

      {totalVendors > size * page && (
        <div id="loader_more_vendors" ref={loader} data-testid="loader" />
      )}

      {(!vendors || vendors?.length === 0) && !loading && (
        <ContainerErrorItems>
          <ErrorVendorList
            error={vendorQcError}
            onAction={
              vendorQcError === STATUS_CODE.NOT_FOUND
                ? redirectToHome
                : reloadLanding
            }
            location={countryId}
            type="item"
          />
        </ContainerErrorItems>
      )}
    </Wrapper>
  );
};

export default QCPartnerList;
